import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArdoqId } from '@ardoq/api-types';
import { documentArchiveAttachments$ } from 'streams/documentArchive/documentArchiveAttachments$';
import { AttachmentWithContext } from 'streams/documentArchive/types';
import { documentArchiveAttachmentOperations } from 'streams/documentArchive/documentArchiveAttachmentOperations';
import { context$ } from 'streams/context/context$';
import { DocumentArchiveContext } from '@ardoq/api-types';
import { Locale, localeCompare } from '@ardoq/locale';
import { I18nLocale, t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';

const getContextWorkspaceAttachments = (
  documentArchiveAttachments: AttachmentWithContext[],
  workspaceId: ArdoqId
) =>
  documentArchiveAttachments.filter(attachment =>
    documentArchiveAttachmentOperations.isOrgOrWorkspaceAttachment(
      attachment,
      workspaceId
    )
  );

const toFileSelectorProps = (
  attachments: AttachmentWithContext[],
  i18nLocale: I18nLocale,
  locale: Locale
) => {
  const [workspaceAttachments, orgAttachments] = attachments.reduce(
    ([workspace, organisation], attachment) =>
      attachment.context === DocumentArchiveContext.WS
        ? [[...workspace, attachment], organisation]
        : [workspace, [...organisation, attachment]],
    [[], []] as [AttachmentWithContext[], AttachmentWithContext[]]
  );

  return {
    options: [
      {
        options: workspaceAttachments
          .map(documentArchiveAttachmentOperations.toSelectOption)
          .sort((a, b) => localeCompare(a.label, b.label, locale)),
        label: t(
          i18nLocale,
          'scopeData:fileSelector.workspaceAttachments',
          'Workspace attachments'
        ),
      },
      {
        options: orgAttachments
          .map(documentArchiveAttachmentOperations.toSelectOption)
          .sort((a, b) => localeCompare(a.label, b.label, locale)),
        label: t(
          i18nLocale,
          'scopeData:fileSelector.globalAttachments',
          'Global attachments'
        ),
      },
    ],
  };
};

const fileOptions$ = combineLatest([
  context$,
  documentArchiveAttachments$,
  i18nLocale$,
  locale$,
]).pipe(
  map(([{ workspaceId }, documentArchive, i18nLocale, locale]) =>
    toFileSelectorProps(
      getContextWorkspaceAttachments(documentArchive.models, workspaceId),
      i18nLocale,
      locale
    )
  )
);

export { fileOptions$ };
